/** Track Management + schedule chip styling (mirrors RailcarCard semantics). */

import {
  amber,
  blue,
  cyan,
  deepPurple,
  green,
  grey,
  indigo,
  red,
} from "@material-ui/core/colors";
import { alpha } from "@material-ui/core/styles";

import {
  InboundState,
  InboundStatus,
  OutboundState,
  OutboundStatus,
} from "./constants";

// TrackListView — type column (inbound / outbound pills)
export const TRACK_LIST_TYPE_INBOUND_BG = "#e3f2fd";
export const TRACK_LIST_TYPE_INBOUND_FG = "#2196f3";
export const TRACK_LIST_TYPE_OUTBOUND_BG = "#fff3e0";
export const TRACK_LIST_TYPE_OUTBOUND_FG = "#ff9800";

// TrackListView — status chip (RELEASED vs active-on-track)
export const TRACK_LIST_STATUS_ACTIVE_BG = "#e8f5e9";
export const TRACK_LIST_STATUS_ACTIVE_FG = "#2e7d32";
export const TRACK_LIST_STATUS_ACTIVE_BORDER = "#4caf50";
export const TRACK_LIST_STATUS_RELEASED_BG = "#ffebee";
export const TRACK_LIST_STATUS_RELEASED_FG = "#c62828";
export const TRACK_LIST_STATUS_RELEASED_BORDER = "#ef5350";

const TRACK_RAILCAR_DEFAULT_BORDER = grey[600];
const TRACK_RAILCAR_RELEASED_BORDER = red[600];
const TRACK_RAILCAR_COMPLETED_BORDER = green[600];

const isScheduleReleasedStatus = (status) =>
  status === InboundStatus.RELEASED ||
  status === OutboundStatus.RELEASED ||
  status === InboundStatus.ARCHIVED;

/** Border / icon color for schedule chips (released, completed, default). */
export const getTrackManagementTimeslotRailcarColor = (event) => {
  const isOutbound = Boolean(event?.outboundId);
  const shipment = event?.inboundShipment || event?.outboundShipment;
  const status = shipment?.status;
  const state = shipment?.state;

  if (status && isScheduleReleasedStatus(status)) {
    return TRACK_RAILCAR_RELEASED_BORDER;
  }
  if (
    (!isOutbound && state === InboundState.COMPLETED) ||
    (isOutbound && state === OutboundState.COMPLETED)
  ) {
    return TRACK_RAILCAR_COMPLETED_BORDER;
  }
  return TRACK_RAILCAR_DEFAULT_BORDER;
};

// Direction color for the schedule direction icon — mirrors the inbound/outbound
// pills used across TrackListView so direction is glanceable, not grey-on-grey.
export const getScheduleDirectionColor = (event) =>
  event?.outboundId ? TRACK_LIST_TYPE_OUTBOUND_FG : TRACK_LIST_TYPE_INBOUND_FG;

// Raw shipment status -> MUI color ramp for the status badge. Keyed by status id
// (not label) since inbound/outbound labels collide (e.g. "Placed" twice).
const SCHEDULE_STATUS_RAMP = {
  IN_TRANSIT: blue,
  SIT_PENDING: amber,
  PENDING: amber,
  ARRIVED: cyan,
  PLACED: indigo,
  SIT_PLACED: indigo,
  LOADED: indigo,
  RELEASED: green,
  ADVICE_FAILED: red,
};

/** Soft status pill (bg + text), theme-aware for the schedule day/week cards. */
export const getScheduleStatusBadgeStyle = (event, theme) => {
  const shipment = event?.inboundShipment || event?.outboundShipment;
  const ramp = SCHEDULE_STATUS_RAMP[shipment?.status] || grey;
  const dark = theme.palette.type === "dark";
  return {
    color: dark ? ramp[300] : ramp[800],
    backgroundColor: dark ? alpha(ramp[400], 0.24) : alpha(ramp[500], 0.16),
  };
};

// Ships Together badge (LTL ship-with, AD-12). deepPurple sits in neither the
// status ramp above (blue/amber/cyan/indigo/green/red) nor the exception glyph
// (orange), so a linked load is never mistaken for a status or an exception.
// Lists and the calendar both read this one pair.
export const getShipsTogetherBadgeStyle = (theme) => {
  const dark = theme?.palette?.type === "dark";
  return {
    color: dark ? deepPurple[200] : deepPurple[800],
    backgroundColor: dark
      ? alpha(deepPurple[400], 0.28)
      : alpha(deepPurple[500], 0.16),
  };
};

// Solid card surface + neutral border so grid blocks read as real cards on the
// dark calendar (not transparent). Status stays signaled by the direction-icon
// color (getTrackManagementTimeslotRailcarColor) and the status text.
export const getTrackManagementTimeslotEventBodyStyle = (theme) => ({
  backgroundColor:
    theme.palette.type === "dark"
      ? theme.palette.grey[800]
      : theme.palette.common.white,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
});
